import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { apiGet } from "../../shared/apiClient";
import { fetchWishlistsList } from "../../shared/commerceMastersApi";
import { IconEye, IconRefresh } from "../../shared/toolbarIcons";
import { RecordViewDrawer } from "../../shared/RecordDrawer";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

function formatMoney(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const dataGridSx = {
  border: "none",
  fontFamily: "inherit",
  fontSize: "0.9rem",
  height: "100%",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f2fcf5",
    borderBottom: "1px solid #e8ecea",
    fontWeight: 600
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #e8ecea",
    backgroundColor: "#fbfffc"
  },
  "& .MuiDataGrid-row:nth-of-type(even):not(.MuiDataGrid-row--editing):not(:hover)": {
    backgroundColor: "#fcfffd"
  },
  "& .MuiDataGrid-cell": {
    borderBottomColor: "#e8ecea"
  }
};

export function WishlistsList() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewRow, setViewRow] = useState(null);

  const customerById = useMemo(() => {
    const m = new Map();
    for (const c of customers) m.set(c.id, c);
    return m;
  }, [customers]);

  const productById = useMemo(() => {
    const m = new Map();
    for (const p of products) m.set(p.id, p);
    return m;
  }, [products]);

  const rows = useMemo(() => {
    return items.map((x) => {
      const customer = customerById.get(x.customerId);
      const product = productById.get(x.productId);
      return {
        id: x.id,
        item: x,
        customerName: customer?.name ?? `#${x.customerId}`,
        productName: product?.name ?? `#${x.productId}`,
        price: formatMoney(x.price),
        date: formatDateTime(x.date),
        active: x.isActive ? "Yes" : "No"
      };
    });
  }, [items, customerById, productById]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [iRes, cRes, pRes] = await Promise.all([fetchWishlistsList(), apiGet("/api/Customers"), apiGet("/api/Products")]);
    if (!iRes.ok) {
      setError(iRes.error ?? "Failed to load wishlists");
      setItems([]);
    } else {
      setItems(iRes.items);
    }
    if (cRes.ok) setCustomers(cRes.data ?? []);
    if (pRes.ok) setProducts(pRes.data ?? []);
    setLoading(false);
  }, []);

  const columns = useMemo(
    () => [
      { field: "customerName", headerName: "Customer", flex: 1, minWidth: 120 },
      { field: "productName", headerName: "Product", flex: 1, minWidth: 120 },
      { field: "price", headerName: "Price", width: 100 },
      { field: "date", headerName: "Date", flex: 0.9, minWidth: 130 },
      { field: "active", headerName: "Active", width: 80 },
      {
        field: "actions",
        headerName: "Actions",
        width: 90,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <div className="table-actions">
            <button className="icon-button" type="button" title="View" onClick={() => setViewRow(params.row)}>
              <IconEye />
            </button>
          </div>
        )
      }
    ],
    []
  );

  useEffect(() => {
    void load();
  }, [load]);

  const total = rows.length;

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Wishlists</h2>
          <p>{loading ? "Loading..." : `Total: ${total}`}</p>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void load()}
            disabled={loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={dataGridSx}
          />
        </div>
      </div>
      <RecordViewDrawer
        open={Boolean(viewRow)}
        onClose={() => setViewRow(null)}
        label="Wishlist details"
        title={viewRow?.productName ?? "—"}
        subtitle={viewRow?.customerName}
        ariaLabel="Wishlist details"
        sections={
          viewRow
            ? [
                {
                  title: "Wishlist Details",
                  columns: 2,
                  fields: [
                    ["Customer", viewRow.customerName, true],
                    ["Product", viewRow.productName, true],
                    ["Price", viewRow.price],
                    ["Date", viewRow.date],
                    ["Active", viewRow.active],
                    ["Customer Id", viewRow.item.customerId ?? "—"],
                    ["Product Id", viewRow.item.productId ?? "—"],
                    ["Quantity", viewRow.item.quantity ?? viewRow.item.qty ?? "—"]
                  ]
                }
              ]
            : []
        }
      />
    </div>
  );
}
